import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { alertActions } from '../redux/AlertController';

export const useSettings = () => {
    const [settings, setSettings] = useState(null);
    const [packages, setPackages] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const dispatch = useDispatch();
    const user = useSelector(state => state.userController.user);

    // Get settings
    const fetchSettings = async () => {
        try {
            setIsLoading(true);
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/settings`, {
                headers: {
                    'Authorization': `Bearer ${user?.token}`
                }
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            setSettings(json);
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    // Update price ranges
    const updatePriceRanges = async (priceRanges) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/settings/price-ranges`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ priceRanges })
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            setSettings(json);
            dispatch(alertActions.showAlert({ msg: 'تم تحديث نطاقات الأسعار بنجاح', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            throw error;
        }
    };

    // Update max search range
    const updateMaxSearchRange = async (maxSearchRangeKm) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/settings/max-search-range`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ maxSearchRangeKm })
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            setSettings(json);
            dispatch(alertActions.showAlert({ msg: 'تم تحديث المسافة القصوى بنجاح', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            throw error;
        }
    };

    // Update referral prize
    const updateReferralPrize = async (referralPrize) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/settings/referral-prize`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ referralPrize })
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            setSettings(json);
            dispatch(alertActions.showAlert({ msg: 'تم تحديث جائزة الإحالة بنجاح', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            throw error;
        }
    };

    // Update joining prize activation
    const updateJoiningPrize = async (joiningPrizeEnabled) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/settings/joining-prize`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ joiningPrizeEnabled })
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            setSettings(json);
            dispatch(alertActions.showAlert({ msg: 'تم تحديث جائزة الانضمام بنجاح', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            throw error;
        }
    };

    // Update emergency numbers list
    const updateEmergencyNumbers = async (emergencyNumbers) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/settings/emergency-numbers`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ emergencyNumbers })
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            setSettings(json);
            dispatch(alertActions.showAlert({ msg: 'تم تحديث أرقام الطوارئ بنجاح', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            throw error;
        }
    };

    // Update app update enforcement config
    const updateAppUpdateConfig = async (appUpdate) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/settings/app-update`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ appUpdate })
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            setSettings(json);
            dispatch(alertActions.showAlert({ msg: 'تم تحديث إعدادات التحديث الإجباري بنجاح', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            throw error;
        }
    };

    // Update role-specific signup terms
    const updateAppTermsConfig = async (appTerms) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/settings/app-terms`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ appTerms })
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            setSettings(json);
            dispatch(alertActions.showAlert({ msg: 'تم تحديث شروط التسجيل بنجاح', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            throw error;
        }
    };

    const updateSocialLinks = async (socialLinks) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/settings/social-links`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ socialLinks })
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            setSettings(json);
            dispatch(alertActions.showAlert({ msg: 'تم تحديث روابط التواصل الاجتماعي', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            throw error;
        }
    };

    // "سياسة جاهز": { user: [points], driver: [points] }
    const updateJaheezPolicy = async (jaheezPolicy) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/settings/jaheez-policy`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ jaheezPolicy })
            });
            const json = await response.json();
            if (!response.ok) {
                throw new Error(json.error);
            }
            setSettings(json);
            dispatch(alertActions.showAlert({ msg: 'تم حفظ سياسة جاهز', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            throw error;
        }
    };

    // Get all packages
    const fetchPackages = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/settings/packages`, {
                headers: {
                    'Authorization': `Bearer ${user?.token}`
                }
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            setPackages(json);
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return null;
        }
    };

    // Create package
    const createPackage = async (packageData) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/settings/packages`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(packageData)
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            dispatch(alertActions.showAlert({ msg: 'تم إنشاء الباقة بنجاح', type: 'success' }));
            await fetchPackages();
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            throw error;
        }
    };

    // Update package
    const updatePackage = async (packageId, packageData) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/settings/packages/${packageId}`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(packageData)
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            dispatch(alertActions.showAlert({ msg: 'تم تحديث الباقة بنجاح', type: 'success' }));
            await fetchPackages();
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            throw error;
        }
    };

    // Delete package
    const deletePackage = async (packageId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/settings/packages/${packageId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${user?.token}`
                }
            });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json.error);
            }

            dispatch(alertActions.showAlert({ msg: 'تم حذف الباقة بنجاح', type: 'success' }));
            await fetchPackages();
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            throw error;
        }
    };

    useEffect(() => {
        if (user?.token) {
            fetchSettings();
            fetchPackages();
        }
    }, [user]);

    const updateDriverSearchConfig = async ({ initialDriverBatch, nextDriverBatch, nextDriverDelaySeconds }) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/settings/driver-search-config`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ initialDriverBatch, nextDriverBatch, nextDriverDelaySeconds })
            });
            const json = await response.json();
            if (!response.ok) throw new Error(json.error);
            setSettings(json);
            dispatch(alertActions.showAlert({ msg: 'تم تحديث إعدادات البحث عن السائقين', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            throw error;
        }
    };

    // Update driver activity scoring / leveling config
    const updateDriverActivityConfig = async (driverActivity) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/settings/driver-activity-config`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(driverActivity)
            });
            const json = await response.json();
            if (!response.ok) throw new Error(json.error);
            setSettings(json);
            dispatch(alertActions.showAlert({ msg: 'تم تحديث إعدادات نقاط نشاط السائقين', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            throw error;
        }
    };

    // Update "average bidding price" hint config
    const updateBiddingDisplayConfig = async (biddingDisplay) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/settings/bidding-display-config`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(biddingDisplay)
            });
            const json = await response.json();
            if (!response.ok) throw new Error(json.error);
            setSettings(json);
            dispatch(alertActions.showAlert({ msg: 'تم تحديث إعدادات متوسط المزايدة', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            throw error;
        }
    };

    // Update pay-per-trip (no-subscription) config
    const updatePayPerTripConfig = async (payPerTrip) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/settings/pay-per-trip-config`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payPerTrip)
            });
            const json = await response.json();
            if (!response.ok) throw new Error(json.error);
            setSettings(json);
            dispatch(alertActions.showAlert({ msg: 'تم تحديث إعدادات الدفع لكل رحلة', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            throw error;
        }
    };

    // Update temporary cancellation-ban config
    const updateCancellationBanConfig = async (cancellationBan) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/settings/cancellation-ban-config`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user?.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(cancellationBan)
            });
            const json = await response.json();
            if (!response.ok) throw new Error(json.error);
            setSettings(json);
            dispatch(alertActions.showAlert({ msg: 'تم تحديث إعدادات إيقاف الإلغاءات المتكررة', type: 'success' }));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            throw error;
        }
    };

    return {
        settings,
        packages,
        isLoading,
        fetchSettings,
        updatePriceRanges,
        updateMaxSearchRange,
        updateReferralPrize,
        updateJoiningPrize,
        updateDriverSearchConfig,
        updateDriverActivityConfig,
        updateBiddingDisplayConfig,
        updatePayPerTripConfig,
        updateCancellationBanConfig,
        updateEmergencyNumbers,
        updateAppUpdateConfig,
        updateAppTermsConfig,
        updateSocialLinks,
        updateJaheezPolicy,
        fetchPackages,
        createPackage,
        updatePackage,
        deletePackage
    };
};